import { useCallback, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Badge,
  Box,
  Button,
  Portal,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';

import DealerRequiredPageShell from 'app/components/ReusableUIComponents/HighLevelComponents/DealerRequiredPageShell';
import DataGridMAIQ, {
  DataGridMAIQRef,
} from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ';
import FilterTrayMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/FilterTrayMAIQ';
import { DealerPageHeaderEntityInfo } from 'app/components/ReusableUIComponents/LowLevelComponents/PageHeaderEntityInfo';
import { withResourceProtection } from 'app/components/ReusableUIComponents/HOCs/ResourceProtected/withResourceProtection';
import { MONITOR_RESOURCE_NURTURING_PLAN_EXECUTION } from 'app/pages/MonitorPage/utils/monitorResourceIds';
import { EmailGenerationProgressCard } from 'app/NewAIAvatarLayout/features/MiaChat/components/EmailGenerationProgressCard';
import { NURTURING_PLAN_EXECUTION_TEST } from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ/components/cells/CellNurturingPlanExecutionActions';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';

import { useNurturingPlanExecutionPage } from './hooks/useNurturingPlanExecutionPage';
import { useNurturingPlanExecutionTestProgress } from './hooks/useNurturingPlanExecutionTestProgress';
import PlanDayTestProgressDialog from './components/PlanDayTestProgressDialog';

const NurturingPlanExecutionPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const dataGridRef = useRef<DataGridMAIQRef>(null);
  const [gridRefreshKey, setGridRefreshKey] = useState('0');
  const [showProgressDetailsModal, setShowProgressDetailsModal] = useState(false);
  const [quickFilterAnchorEl, setQuickFilterAnchorEl] = useState<HTMLElement | null>(
    null,
  );

  const {
    breadcrumbsList,
    hasDealer,
    dealerTitle,
    dealerId,
    executionsGridType,
    handleGridMetaChange,
    isQuickFilterOpen,
    setIsQuickFilterOpen,
    filterTray,
  } = useNurturingPlanExecutionPage();

  const handleTestComplete = useCallback(() => {
    setGridRefreshKey(Date.now().toString());
  }, []);

  const handleTestError = useCallback(
    (message: string) => {
      setShowProgressDetailsModal(false);
      dispatch(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'error',
          message,
        }),
      );
    },
    [dispatch],
  );

  const {
    activeJobId,
    activeRunId,
    activePlanDay,
    isTracking,
    isFinal,
    phase,
    currentSection,
    progressMessage,
    progressOverride,
    latestJobPayload,
    latestRunEvent,
    errorMessage,
    startTestPlanDay,
    resetProgress,
  } = useNurturingPlanExecutionTestProgress({
    dealerId: dealerId ? String(dealerId) : undefined,
    onComplete: handleTestComplete,
    onError: handleTestError,
  });

  const handleCellClick = useCallback(
    async (cell: any, clickedElement?: string) => {
      if (clickedElement !== NURTURING_PLAN_EXECUTION_TEST) return;
      if (!cell?.row?.plan_day) return;

      const rowKey = cell.row.__gridRowId;
      if (rowKey != null) {
        dataGridRef.current?.manualUpdateData(String(rowKey), { __testing: true });
      }

      try {
        await startTestPlanDay(Number(cell.row.plan_day));
      } catch (error: any) {
        resetProgress();
        if (rowKey != null) {
          dataGridRef.current?.manualUpdateData(String(rowKey), { __testing: false });
        }
        dispatch(
          notificationAlertActions.addNotification({
            open: true,
            severity: 'error',
            message: error?.message || 'Failed to start plan day test',
          }),
        );
      } finally {
        if (rowKey != null) {
          dataGridRef.current?.manualUpdateData(String(rowKey), { __testing: false });
        }
      }
    },
    [dispatch, resetProgress, startTestPlanDay],
  );

  const handleViewRunLogs = useCallback(() => {
    if (!activeRunId) return;
    history.push('/run-logs', {
      runId: activeRunId,
      subscribeRunId: true,
      breadcrumbsList: [
        { label: 'Monitor', link: '/monitor' },
        { label: 'Nurturing Plan Execution', link: '/monitor-nurturing-plan-execution' },
        { label: 'Run Logs', isActive: true },
      ],
    });
    resetProgress();
  }, [activeRunId, breadcrumbsList, history, resetProgress]);

  const canViewRunLogs = Boolean(activeRunId && isFinal && !errorMessage);

  const handleOpenQuickFilter = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setQuickFilterAnchorEl(event.currentTarget);
      setIsQuickFilterOpen(true);
    },
    [setIsQuickFilterOpen],
  );

  const handleCloseQuickFilter = useCallback(() => {
    setIsQuickFilterOpen(false);
    setQuickFilterAnchorEl(null);
  }, [setIsQuickFilterOpen]);

  const gridKey = [
    dealerId ?? '',
    executionsGridType?.renderCellMapping?.length ?? 0,
    gridRefreshKey,
    JSON.stringify(filterTray.executionFilterModel ?? {}),
  ].join('|');

  return (
    <>
      <Helmet>
        <title>Nurturing Plan Execution</title>
        <meta
          name="description"
          content="Monitor nurturing plan execution runs for the selected dealership"
        />
      </Helmet>
      <DealerRequiredPageShell
        hasDealer={hasDealer}
        breadcrumbsList={breadcrumbsList}
        pageTitle="Nurturing Plan Execution"
        headerSubtitle={
          hasDealer ? (
            <DealerPageHeaderEntityInfo
              title={dealerTitle}
              dealerId={dealerId}
            />
          ) : undefined
        }
        contentVariant="fluid"
      >
        {executionsGridType ? (
          <Box
            sx={{
              width: '100%',
              maxWidth: 1440,
              mx: 'auto',
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              backgroundColor: 'background.paper',
              borderRadius: 1.5,
              boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                pt: 1.5,
                px: 1.5,
                pb: 1,
                flexShrink: 0,
              }}
            >
              <Badge
                badgeContent={
                  filterTray.totalActiveCount > 0
                    ? filterTray.totalActiveCount
                    : undefined
                }
                color="primary"
                sx={{ '& .MuiBadge-badge': { fontSize: 10 } }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleOpenQuickFilter}
                  startIcon={<FilterListIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  Quick Filters
                </Button>
              </Badge>
            </Box>
            <DataGridMAIQ
              ref={dataGridRef}
              key={gridKey}
              type={executionsGridType}
              refreshKey={gridRefreshKey}
              onCellClick={handleCellClick}
              onGridMetaChange={handleGridMetaChange}
            />
            <FilterTrayMAIQ
              isLoading={filterTray.isLoading}
              open={isQuickFilterOpen}
              onClose={handleCloseQuickFilter}
              anchorEl={quickFilterAnchorEl}
              categories={filterTray.categories}
              activeCategoryId={filterTray.activeCategoryId}
              onActiveCategoryChange={filterTray.setActiveCategoryId}
              optionsForActiveCategory={filterTray.optionsForActiveCategory}
              selectedByCategory={filterTray.selectedByCategory}
              onSelectionChange={filterTray.onSelectionChange}
              totalActiveCount={filterTray.totalActiveCount}
              onClearAllFilters={filterTray.onClearAllFilters}
              onClearActiveCategory={filterTray.onClearActiveCategory}
              onClearAllInPanel={filterTray.onClearAllInPanel}
            />
          </Box>
        ) : null}
      </DealerRequiredPageShell>

      {isTracking && (
        <Portal>
          <Box
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1300,
              minWidth: 520,
            }}
          >
            <EmailGenerationProgressCard
              isGeneratingEmail={!isFinal}
              title={
                activePlanDay != null
                  ? `Testing plan day ${activePlanDay}`
                  : 'Testing nurturing plan day'
              }
              currentSection={currentSection}
              progressOverride={progressOverride}
              onOpenEmailGenerationPopup={() => setShowProgressDetailsModal(true)}
            />
            {canViewRunLogs ? (
              <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" size="small" onClick={handleViewRunLogs}>
                  View Run Logs
                </Button>
              </Box>
            ) : isFinal ? (
              <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="outlined" size="small" onClick={resetProgress}>
                  Close
                </Button>
              </Box>
            ) : null}
          </Box>
        </Portal>
      )}

      <PlanDayTestProgressDialog
        open={showProgressDetailsModal}
        onClose={() => setShowProgressDetailsModal(false)}
        activePlanDay={activePlanDay}
        activeJobId={activeJobId}
        activeRunId={activeRunId}
        isFinal={isFinal}
        phase={phase}
        currentSection={currentSection}
        progressMessage={progressMessage}
        progressOverride={progressOverride}
        latestJobPayload={latestJobPayload}
        latestRunEvent={latestRunEvent}
        errorMessage={errorMessage}
        canViewRunLogs={canViewRunLogs}
        onViewRunLogs={handleViewRunLogs}
      />
    </>
  );
};

export default withResourceProtection(
  NurturingPlanExecutionPage,
  MONITOR_RESOURCE_NURTURING_PLAN_EXECUTION,
);
